import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useAppSettings } from "@/context/SettingsContext";
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut, 
  FileText, Edit3, BarChart3, UserCog, Image, X, Globe, Scissors, HardHat, Settings, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ViewToggle from "@/components/ViewToggle";
import { SubscriptionsToggle } from "@/components/SubscriptionsToggle";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

const NavItem = ({ item, location, onNavigate }) => {
  const isActive = location.pathname + (item.path.includes("?") ? location.search : "") === item.path;
  const hasNotifications = item.isNotification && item.badge > 0 && !isActive;
  return (
    <li>
      <Link
        to={item.path}
        onClick={onNavigate}
        data-testid={item.testId || `sidebar-${item.path.replace("/", "")}`}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${isActive
          ? "bg-[#39FF14] text-black font-bold"
          : hasNotifications
            ? "text-[#FF2D2D] bg-[#FF2D2D]/10 hover:bg-[#FF2D2D]/20 font-semibold animate-pulse"
            : "text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"}`}
      >
        <item.icon className={`w-5 h-5 ${hasNotifications ? "text-[#FF2D2D]" : ""}`} />
        <span className="flex-1">{item.label}</span>
        {item.badge > 0 && (
          <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isActive
            ? "bg-black text-[#39FF14]"
            : item.isNotification ? "bg-[#FF2D2D] text-white shadow-[0_0_10px_rgba(255,45,45,0.5)]" : "bg-[#39FF14] text-black"}`}>
            {item.badge}
          </span>
        )}
      </Link>
    </li>
  );
};

const AdminSidebar = ({ sidebarOpen, setSidebarOpen, pendingQuotes = 0, notificationCount = 0 }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  const { subscriptionsEnabled } = useAppSettings();

  const mainItems = [
    { icon: LayoutDashboard, label: t("admin.dashboard", "Dashboard"), path: "/dashboard" },
    { icon: Bell, label: t("admin.notifications", "Notifications"), path: "/notifications", badge: notificationCount, isNotification: true },
    { icon: FileText, label: t("admin.quotes", "Quotes"), path: "/quotes", badge: pendingQuotes },
    { icon: Scissors, label: t("admin.cutRequests", "Cut Requests"), path: "/cut-requests" },
    { icon: Users, label: t("admin.customers", "Customers"), path: "/customers" },
    { icon: Calendar, label: t("admin.schedule", "Schedule"), path: "/schedule" },
  ];
  const moreItems = [
    { icon: FileText, label: t("admin.cutReport", "Cut Report"), path: "/cut-report" },
    { icon: BarChart3, label: t("admin.analytics", "Analytics"), path: "/analytics" },
    subscriptionsEnabled
      ? { icon: CreditCard, label: t("admin.subscriptions", "Subscriptions"), path: "/subscriptions" }
      : null,
    { icon: Settings, label: "Settings & Text Alerts", path: "/subscriptions?tab=settings", testId: "sidebar-settings" },
    { icon: HardHat, label: t("admin.staff", "Staff"), path: "/staff-management" },
    { icon: UserCog, label: t("admin.userManagement", "User Management"), path: "/users" },
    { icon: Edit3, label: t("admin.contentManager", "Content Manager"), path: "/content-manager" },
    { icon: Globe, label: t("admin.translations", "Translations"), path: "/translations" },
    { icon: Image, label: t("admin.flyerGenerator", "Flyer Generator"), path: "/flyer-generator" },
  ].filter(Boolean);
  const [showMore, setShowMore] = useState(moreItems.some((i) => i.path.split("?")[0] === location.pathname));

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#121212] border-r border-[#27272A]
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-[#27272A] flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Zig Zag Lawncare" className="w-10 h-10 rounded-lg" />
            <span className="font-heading text-white text-lg uppercase">Admin</span>
          </Link>
          <button 
            className="lg:hidden text-[#A1A1AA] hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Admin / Staff view toggle */}
        <div className="px-4 pt-4 space-y-3">
          <ViewToggle />
          <SubscriptionsToggle />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {mainItems.map((item) => <NavItem key={item.path} item={item} location={location} onNavigate={() => setSidebarOpen(false)} />)}
          </ul>
          <button
            type="button"
            data-testid="sidebar-more-toggle"
            onClick={() => setShowMore(!showMore)}
            className="w-full flex items-center justify-between px-4 py-2 mt-4 text-[#71717A] text-xs uppercase tracking-wider font-bold hover:text-white transition-colors"
          >
            More tools
            <ChevronDown className={`w-4 h-4 transition-transform ${showMore ? "rotate-180" : ""}`} />
          </button>
          {showMore && (
            <ul className="space-y-1" data-testid="sidebar-more-list">
              {moreItems.map((item) => <NavItem key={item.path} item={item} location={location} onNavigate={() => setSidebarOpen(false)} />)}
            </ul>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-[#27272A]">
          <div className="flex items-center gap-3 mb-4 px-2">
            <Avatar className="w-10 h-10 border-2 border-[#39FF14]">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-[#39FF14] text-black font-bold">
                {user?.name?.[0] || "A"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name || "Admin"}</p>
              <p className="text-[#A1A1AA] text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {t("common.logout", "Logout")}
          </Button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
